import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
	Button,
	Card,
	Checkbox,
	Col,
	Collapse,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Modal,
	notification,
	Popconfirm,
	Skeleton,
	Row,
	Select, Typography,
	Tooltip,
} from 'antd';
import {
	useAddMaterialOrderLineItemFromMaterialRequestMutation,
	useAddMaterialOrderLineItemFromVendorMaterialMutation,
	useAddMaterialOrderMutation,
	useDeleteMaterialOrderMutation,
	useGetMaterialOrderByIdQuery,
	useGetMaterialVendorsQuery,
	usePatchMaterialOrderMutation,
	useRemoveMaterialOrderLineItemFromDraftMutation,
	useToggleMergeMutation,
	useUpdateMaterialOrderLineItemMutation,
	useUpdateMaterialOrderMutation,
} from '../../../redux/api/material';
import { useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router';
import dayjs, { Dayjs } from 'dayjs';
import { Link } from 'react-router-dom';
import AddFromMaterialRequest from '../../../components/modals/AddFromMaterialRequest';
import { useDispatch } from 'react-redux';
import {
	IMaterialOrder,
	IMaterialOrderLineItem,
	IVendorMaterial,
	MaterialTypeObject,
} from '../../../interfaces/Material.Interface';
import ShowPrices from '../../../components/ShowPrices';
import { MaterialOrderStatusSteps } from '../MaterialOrderStatusSteps.tsx';
import Header from '../../../components/Header';
import { MaterialOrderRequestsRelated } from '../MaterialOrderRequestsRelated.tsx';
import AddFromVendorMaterials from '../../../components/modals/AddFromVendorMaterials';
import { BarVisualizer } from '../BarVisualizer.tsx';
import { debounce } from 'lodash';
import EditableNumberCell from "../../../components/Commons/EditableNumberCell.tsx";
import { useGetBuildingsQuery } from '../../../redux/api/location';

const { confirm } = Modal;
const { Panel } = Collapse;

const AddMaterialOrder = () => {
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const { id } = useParams();
	const {
		data,
		refetch,
		isLoading: isLoadingMaterialOrder,
		isFetching,
	} = useGetMaterialOrderByIdQuery({ id: id as string }, { skip: !id });

	const dispatch = useDispatch();

	const materialVendorsQuery = useGetMaterialVendorsQuery();
	const { data: buildings } = useGetBuildingsQuery();
	const [orderDate, setOrderDate] = useState<Dayjs>(dayjs());
	const [addMaterialOrder, result] = useAddMaterialOrderMutation();
	const [updateMaterialOrder, updateMaterialOrderResult] =
		useUpdateMaterialOrderMutation();
	const [deleteMaterialOrder, deleteMaterialOrderResult] =
		useDeleteMaterialOrderMutation();
	const [patchMaterialOrderLineItem] = useUpdateMaterialOrderLineItemMutation();
	const [removeLineItemFromDraft] =
		useRemoveMaterialOrderLineItemFromDraftMutation();
	const [addMaterialOrderLineItemFromMaterialRequest] =
		useAddMaterialOrderLineItemFromMaterialRequestMutation();
	const [addMaterialOrderLineItemFromVendorMaterial] =
		useAddMaterialOrderLineItemFromVendorMaterialMutation();
	const [patchMaterialOrder] = usePatchMaterialOrderMutation();
	const [idsMaterialRequestsRelated, setIdsMaterialRequestsRelated] = useState<
		string[]
	>([]);
	const [materialOrderLineItems, setMaterialOrderLineItems] = useState<
		Array<IMaterialOrderLineItem>
	>([]);
	const [showAddFromMaterialRequestModal, setShowAddFromMaterialRequestModal] =
		useState<boolean>(false);
	const [
		showMaterialRequestsRelatedModal,
		setShowMaterialRequestsRelatedModal,
	] = useState<boolean>(false);
	const [showVendorMaterialsModal, setShowVendorMaterialsModal] =
		useState<boolean>(false);

	const [materialOrderStatus, setMaterialOrderStatus] =
		useState<string>('Draft');
	const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(
		null
	);

	useEffect(() => {
		if (result.isSuccess) {
			navigate(`/material/orders/${result?.data?.id}`);
		}
	}, [result]);

	useEffect(() => {
		if (id && data) {
			form.setFieldValue('vendor_order_number', data.vendor_order_number);
			form.setFieldValue('vendor', data.vendor ? data.vendor.id : '');
			form.setFieldValue('building_id', data.building?.id ?? undefined);
			form.setFieldValue('order_date', dayjs(data.order_date || dayjs()));
			form.setFieldValue('create_purchase_order', data.create_purchase_order);
			form.setFieldValue('requisition_number', data.requisition_number);
			form.setFieldValue('shipping_cost', data.shipping_cost);
			form.setFieldValue('building_id', data.building?.id ?? null);
			setMaterialOrderStatus(data.status);
			setSelectedBuildingId(data.building?.id ?? null);
			handleSetMaterialOrderLineItems(data);
		}
	}, [id, data]);

	useEffect(() => {
		if (updateMaterialOrderResult.isSuccess) {
			if (updateMaterialOrderResult.data.status === 'Draft') {
				notification.success({
					message: 'Draft Saved Successfully',
					placement: 'top',
				});
			} else {
				notification.success({
					message: 'Material Order Submitted Successfully',
					placement: 'top',
				});
			}

			return navigate(`/material/orders/${id}`);
		}
		if (updateMaterialOrderResult.isError) {
			notification.error({
				message: 'Something went wrong',
				placement: 'topLeft',
			});
		}
	}, [updateMaterialOrderResult]);

	useEffect(() => {
		if (deleteMaterialOrderResult.isSuccess) {
			navigate(`/material/orders`);
		}
	}, [deleteMaterialOrderResult]);

	const onFinish = async (status: string) => {
		await form
			.validateFields()
			.then(async (values) => {
				const orderDate = values.order_date;
				const payload = {
					status: status,
					total_amount: Number(totals.totalAmount.toFixed(2)) || 0,
					...(orderDate != null && {
						order_date:
							typeof orderDate?.toISOString === 'function'
								? orderDate.toISOString()
								: orderDate,
					}),
				};

				await patchMaterialOrder({
					id: id,
					values: payload,
				})
					.unwrap()
					.then((res) => {
						notification.success({
							duration: 5,
							message: 'Success',
							description:
								status === 'Draft'
									? 'Draft saved successfully'
									: status === 'In Progress'
									? 'Material Order saved successfully'
									: 'Material Order submitted successfully',
							placement: 'topRight',
						});

						if (res['status'] === 'Submitted') {
							navigate(`/material/orders/${id}`);
						}
					})
					.catch((error) => {
						notification.error({
							duration: 5,
							message: 'Error',
							description: `Error encountered while attempting to submit.`,
							placement: 'topRight',
						});
					});
			})
			.catch((errorInfo) => {
				form.scrollToField(errorInfo.errorFields[0].name);
				notification.error({
					duration: 10,
					message: 'Error',
					description: `Required fields are empty!`,
					placement: 'topRight',
				});
			});
	};

	const showConfirm = async () => {
		confirm({
			title: 'Delete Draft Material Order',
			content:
				'This action will permanently delete the Draft. Once deleted it cannot be recovered',
			onOk: async () => {
				await deleteMaterialOrder({ id: id });
			},
			okText: 'Yes, Delete',
			okType: 'danger',
		});
	};

	const handleSetMaterialOrderLineItems = async (data) => {
		if (
			data?.material &&
			data?.material.length &&
			materialOrderLineItems.length === 0
		) {
			data?.material.map((material: any) => {
				materialOrderLineItems.push({
					id: material.id,
					display_name: material.display_name,
					quantity_ordered: material.quantity_ordered,
					length: material.length,
					vendor_part_number: material.vendor_part_number,
					unit_price: material.unit_price,
					promised_delivery_date: material.promised_delivery_date
						? dayjs(material.promised_delivery_date)
						: undefined,
					minimum_quantity: material.minimum_quantity,
					vendor_material_length: material.vendor_material_length,
					material_order_requests: material.material_order_requests,
					material_order: material.material_order,
					is_merged: material.is_merged,
					shape: material.shape,
					min_length: material.min_length,
				});
			});

			materialOrderLineItems.sort((a, b) => {
				if (a.vendor_part_number)
					return a.vendor_part_number.localeCompare(
						b.vendor_part_number as string
					);
			});

			refreshTotals(data?.shipping_cost);
		}
	};

	const isDraft = () => {
		return data?.status === 'Draft';
	};

	const isDisabled = () => {
		return data?.status != 'Draft' && data?.status != 'In Progress';
	};

	const updateMaterialOrderLineItem = async (
		index: number,
		key: string,
		value: string | number | any | null
	) => {
		const array = materialOrderLineItems.filter((item: any, i: number) => {
			if (i === index) {
				item[key] = value;
			}
			return item;
		});
		// Update the state
		setMaterialOrderLineItems(array);
		refreshTotals();
		const item = materialOrderLineItems[index];
		const payload = {
			[key]: value,
		};
		await patchMaterialOrderLineItem({ id: item.id, values: payload });
	};

	// Store the totals in the state
	const [totals, setTotals] = useState({
		subTotal: 0,
		shippingCost: 0,
		totalAmount: 0,
	});

	const refreshTotals = (shipping_cost?: number) => {
		// Calculate the values for the subTotal, shippingCost and totalAmount
		const subTotal = getSubTotal();
		const totalAmount =
			Number(subTotal) + Number(shipping_cost || totals.shippingCost);
		setTotals({
			subTotal: Number(subTotal),
			shippingCost:
				Math.round(Number(shipping_cost || totals.shippingCost) * 100) / 100,
			totalAmount: Math.round(totalAmount * 100) / 100,
		});
	};

	const getSubTotal = () => {
		let subTotal = 0;
		materialOrderLineItems.forEach((item: IMaterialOrderLineItem) => {
			if (item.quantity_ordered && item.unit_price) {
				subTotal += item.quantity_ordered * item.unit_price;
			}
		});
		return Math.round(subTotal * 100) / 100;
	};

	const isRoundUp = (vendor_part_number) => {
		const duplicates = materialOrderLineItems.filter(
			(item) => item.vendor_part_number === vendor_part_number
		);
		return {
			isDuplicate: duplicates.length > 1,
			duplicateIds: duplicates.map((item) => item.id),
		};
	};

	const [toggleMerge, { isLoading }] = useToggleMergeMutation();

	const handleCheckboxChange = async (
		e,
		material_order_id,
		material_order_requests,
		vendor_part_number
	) => {
		const isChecked = e.target.checked;
		try {
			await toggleMerge({
				material_order_id: material_order_id,
				vendor_part_number: vendor_part_number,
				is_checked: isChecked,
			}).unwrap();

			setMaterialOrderLineItems([]);
			const updatedData = await refetch().unwrap();
			await handleSetMaterialOrderLineItems(updatedData);
		} catch (error) {
			console.error('Error applying merge/split:', error);
		}
	};

	/**
	 * A line built from material requests is one bar of a cut plan, so removing
	 * it sends those requests back to the pool and packs the rest of the order
	 * again. The whole table is rebuilt from the response, the same way a
	 * merge/split is.
	 */
	const handleDeleteMaterialOrderLineItem = async (lineItemId: string) => {
		try {
			await removeLineItemFromDraft({
				material_order_id: id,
				line_item_id: lineItemId,
			}).unwrap();

			setMaterialOrderLineItems([]);
			const updatedData = await refetch().unwrap();
			await handleSetMaterialOrderLineItems(updatedData);
		} catch (error: any) {
			const reason = Array.isArray(error?.data) ? error.data[0] : error?.data;

			notification.error({
				duration: 10,
				message: 'Error',
				description:
					typeof reason === 'string' ? reason : 'Something went wrong',
				placement: 'topRight',
			});
		}
	};

	/**
	 * Removing a row built from material requests takes every row of that
	 * vendor part with it, since the cut plan is packed again from what is
	 * left.
	 */
	const rowsRemovedWith = (item: IMaterialOrderLineItem) =>
		item.material_order_requests?.length
			? materialOrderLineItems.filter(
					(row) =>
						row.material_order_requests?.length &&
						row.vendor_part_number === item.vendor_part_number
				)
			: [item];

	/** An order with nothing on it is not a state worth reaching. */
	const removalWouldEmptyOrder = (item: IMaterialOrderLineItem) =>
		materialOrderLineItems.length <= rowsRemovedWith(item).length;

	/**
	 * Naming the rows and requests that go: a generic "delete this item"
	 * undersells a click that can clear three rows and repack the order.
	 */
	const removalConfirmTitle = (item: IMaterialOrderLineItem) => {
		if (!item.material_order_requests?.length) {
			return 'Are you sure you want to delete this item?';
		}

		const removed = rowsRemovedWith(item);
		// Every row of a vendor part carries that part's full request set, so
		// summing per row counts each request once per row. Count distinct ids.
		const requests = new Set(
			removed.flatMap((row) => row.material_order_requests ?? [])
		).size;

		const lines =
			removed.length > 1
				? `all ${removed.length} lines of ${item.vendor_part_number}`
				: 'this line';

		return `This takes ${lines} off the order, returns ${requests} material request${
			requests === 1 ? '' : 's'
		} to the pool and rebuilds the cut plan. Continue?`;
	};

	const addNewItemFromMaterialRequest = async (
		requests: MaterialTypeObject[]
	) => {
		const selectedRows = requests.map((item) => {
			return {
				vendor: item.vendor_material?.vendor?.id,
				type: item.vendor_material?.type?.id,
				shape: item.vendor_material?.shape?.id,
				quantity_minimum: 0,
				quantity_ordered: 0,
				material_order_request: item.id,
				length: item.total_required_length,
				status: 'Draft',
				vendor_part_number: item.vendor_material?.vendor_part_number,
			};
		});

		await addMaterialOrderLineItemFromMaterialRequest({
			material_order_id: id,
			material_order_items: selectedRows,
		})
			.unwrap()
			.then((res) => {
				setMaterialOrderLineItems([]);
				const updatedData = refetch().unwrap();
				handleSetMaterialOrderLineItems(updatedData);
			})
			.catch(() => {
				notification.error({
					duration: 10,
					message: 'Error',
					description: 'The material request cannot be converted.',
					placement: 'topRight',
				});
			});
	};

	const addNewItemFromVendorMaterials = async (
		selectedRows: IVendorMaterial[]
	) => {
		const newItems = selectedRows.map((item) => {
			return {
				vendor: item.vendor.id,
				type: item.type.id,
				shape: item.shape.id,
				quantity_minimum: 0,
				quantity_ordered: 0,
				material_order_request: null,
				length: item.lengths[0].length,
				status: 'Draft',
				vendor_part_number: item.vendor_part_number,
				unit_price: item.lengths[0].price,
			};
		});

		await addMaterialOrderLineItemFromVendorMaterial({
			material_order_id: id,
			material_order_items: newItems,
		})
			.unwrap()
			.then((res) => {
				setMaterialOrderLineItems([]);
				const updatedData = refetch().unwrap();
				handleSetMaterialOrderLineItems(updatedData);
			})
			.catch(() => {
				notification.error({
					duration: 10,
					message: 'Error',
					description: 'The material request cannot be converted.',
					placement: 'topRight',
				});
			});
	};

	const handleGetMaterialRequestsIds = () => {
		const material_requests_ids = [];
		materialOrderLineItems.map((item) => {
			item.material_order_requests?.map((request) => {
				material_requests_ids.push(request);
			});
		});
		return material_requests_ids;
	};

	const handleGetVendorMaterialsPartNumbers = () => {
		const vendor_materials_part_numbers = [];
		materialOrderLineItems.map((item) => {
			vendor_materials_part_numbers.push(item.vendor_part_number);
		});
		return vendor_materials_part_numbers;
	};

	const getAllMaterialOrderRequests = (vendor_part_number) => {
		const allMaterialOrderRequests = [];
		materialOrderLineItems.forEach((item) => {
			if (
				item.vendor_part_number === vendor_part_number &&
				item.material_order_requests
			) {
				allMaterialOrderRequests.push(...item.material_order_requests);
			}
		});
		return allMaterialOrderRequests;
	};

	const itemReferences = new Map(
		data?.material_order_requests?.map((request) => [
			request.order_line_item_id,
			request.order_line_item_name,
		])
	);
	const selectedBuildingName = buildings?.find(
		(building) => building.id === selectedBuildingId
	)?.name;
	const addFromMaterialRequestDisabledReason = !data?.vendor
		? 'Select a material vendor before adding from material requests.'
		: !isDraft()
		? 'Material requests can only be added while the order is in Draft.'
		: !selectedBuildingId
		? 'Select a building in Order Details before adding from material requests.'
		: undefined;

	const debouncedUpdateMaterial = useMemo(
		() =>
			debounce(async (field: string, value: string | any) => {
				const payload = {
					field,
					[field]: value,
				};
				await patchMaterialOrder({
					id: id,
					values: payload,
				});
			}, 500),
		[]
	);

	return (
		<>
			<Header
				title="Edit Material Order"
				showLinkButton={false}
				icon={<PlusOutlined />}
				breadcrumbItems={[
					{
						title: <Link to={`/`}>Home</Link>,
					},
					{
						title: <Link to={`/material/orders`}>Material Orders</Link>,
					},
					{
						title: 'Edit Material Order',
					},
				]}
			/>

			<Form
				form={form}
				name="dynamic_form_complex"
				layout="vertical"
				autoComplete="off"
			>
			<Card title="Order Details">
				<Row gutter={[16, 8]}>
					<Col xs={24} sm={12} lg={6}>
						<Form.Item
							name="vendor"
							label="Material Vendor"
							rules={[{ required: true, message: 'Required' }]}
						>
							<Select
								placeholder="Select a vendor"
								disabled={true}
								onSelect={async (value) => {
									if (value) await onFinish(data.status);
								}}
							>
								{materialVendorsQuery?.data?.map((type, index) => (
									<Select.Option key={index} value={type.id}>
										{type?.display_name}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>

				<Col xs={24} sm={12} lg={6}>
					<Form.Item
						name="building_id"
						label="Building"
						rules={[{ required: true, message: 'Building is required' }]}
					>
						<Select
							disabled={!isDraft() || (data?.material_order_requests?.length ?? 0) > 0}
							placeholder="Select building"
							allowClear
							options={buildings?.map((b) => ({
								value: b.id,
								label: b.name,
							}))}
							onChange={(value) => {
								setSelectedBuildingId(value ?? null);
								form.setFieldValue('building_id', value ?? undefined);
								debouncedUpdateMaterial('building_id', value ?? null);
							}}
						/>
					</Form.Item>
				</Col>

					<Col xs={24} sm={12} lg={6}>
						<Form.Item name="requisition_number" label="Requisition Number">
							<Input
								disabled={isDisabled()}
								onChange={(e) =>
									debouncedUpdateMaterial('requisition_number', e.target.value)
								}
							/>
						</Form.Item>
					</Col>

					<Col xs={24} sm={12} lg={6}>
						<Form.Item name="vendor_order_number" label="Vendor Order Number">
							<Input
								disabled={isDisabled()}
								onChange={(e) =>
									debouncedUpdateMaterial('vendor_order_number', e.target.value)
								}
							/>
						</Form.Item>
					</Col>

					<Col xs={24} sm={12} lg={6}>
						<Form.Item
							name="order_date"
							label="Order Date"
							initialValue={orderDate}
							rules={[{ required: true, message: 'Required' }]}
						>
							<DatePicker
								disabled={isDisabled()}
								className="w-full"
								onChange={(value) => {
									if (value) setOrderDate(value);
									debouncedUpdateMaterial(
										'order_date',
										value?.toISOString?.() ?? value
									);
								}}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider style={{ margin: '8px 0' }} />

				<div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500">
					<span>
						<span className="font-medium text-gray-700">Created by:</span>{' '}
						{data?.created_by ?? '-'} &middot;{' '}
						{data?.created_time
							? dayjs(data.created_time).format('MMM D, YYYY h:mm A')
							: '-'}
					</span>
					<span>
						<span className="font-medium text-gray-700">Modified by:</span>{' '}
						{data?.modified_by ?? '-'} &middot;{' '}
						{data?.modified_time
							? dayjs(data.modified_time).format('MMM D, YYYY h:mm A')
							: '-'}
					</span>
				</div>
			</Card>

				<div style={{ margin: 0, padding: '1rem 0' }}>
					<Card styles={{ body: { padding: '1rem' } }}>
						<Collapse ghost>
							<Panel header="Material Request Breakdown" key="1">
								{isLoadingMaterialOrder || isFetching || isLoading ? (
									<Skeleton />
								) : data?.material_request_breakdown ? (
									<BarVisualizer
										data={data.material_request_breakdown}
										item_references={itemReferences}
									/>
								) : null}
							</Panel>
						</Collapse>
					</Card>
				</div>

				<Card
					title={'Line Items'}
					loading={isLoading || isFetching || isLoadingMaterialOrder}
				>
					<div className="overflow-x-auto">
						<table className="w-full border">
							<thead>
								<tr className="border-b">
									<th
										className="p-2 border-r cursor-pointer text-sm"
										style={{ minWidth: '60px' }}
									>
										<div className="flex items-center justify-center">#</div>
									</th>
									<th className="p-2 border-r cursor-pointer text-sm">
										<div className="flex items-center justify-center">
											Part #
										</div>
									</th>
								<th className="p-2 border-r cursor-pointer text-sm">
									<div className="flex items-center justify-center">
										Material
									</div>
								</th>
							<th
								className="p-2 border-r cursor-pointer text-sm"
								style={{ minWidth: '135px' }}
							>
								<div className="flex items-center justify-center">
									Unit Price
								</div>
							</th>
									<th
										className="p-2 border-r cursor-pointer text-sm"
										style={{ minWidth: '135px' }}
									>
										<div className="flex items-center justify-center">
											Minimum Quantity
										</div>
									</th>
									<th
										className="p-2 border-r cursor-pointer text-sm"
										style={{ minWidth: '135px' }}
									>
										<div className="flex items-center justify-center">
											Order Quantity
										</div>
									</th>
									<th
										className="p-2 border-r cursor-pointer text-sm"
										style={{ minWidth: '135px' }}
									>
										<div className="flex items-center justify-center">
											Total Price
										</div>
									</th>
									<th
										className="p-2 border-r cursor-pointer text-sm"
										style={{ minWidth: '135px' }}
									>
										<div className="flex items-center justify-center">
											Length
										</div>
									</th>
									<th
										className="p-2 border-r cursor-pointer text-sm"
										style={{ minWidth: '135px' }}
									>
										<div className="flex items-center justify-center">
											Total Length
										</div>
									</th>

									<th className="p-2 border-r cursor-pointer text-sm">
										<div className="flex items-center justify-center">
											Promise Delivery Date
										</div>
									</th>
									<th
										className="p-2 border-r cursor-pointer text-sm"
										style={{ minWidth: '115px' }}
									>
										<div className="flex items-center justify-center">
											Round Up
										</div>
									</th>
								<th className="p-2 border-r cursor-pointer text-sm">
									<div className="flex items-center justify-center">
										Material Requests
									</div>
								</th>
							{/*<th className="p-2 border">*/}
								{/*	<div className="flex items-center justify-center">*/}
								{/*		Actions*/}
								{/*	</div>*/}
								{/*</th>*/}
							</tr>
						</thead>
							<tbody>
								{materialOrderLineItems.map((item, index) => {
									const { isDuplicate, duplicateIds } = isRoundUp(
										item.vendor_part_number
									);
									const isFirstOccurrence = duplicateIds[0] === item.id;

									return (
										<tr key={item.id} className="text-center border-b text-sm">
											<td className="p-2 border-r">{index + 1}</td>
											<td className="p-2 border-r">
												{item.vendor_part_number}
											</td>
										<td className="p-2 border-r">
											<b>{item.display_name}</b>
											{item.shape?.display_name && (
												<span> ({item.shape?.display_name})</span>
											)}
										</td>
									<td className="p-2 border-r">
										<InputNumber
											min={0}
											disabled={isDisabled()}
											value={item.unit_price}
													onChange={(value) =>
														updateMaterialOrderLineItem(
															index,
															'unit_price',
															value
														)
													}
												/>
											</td>
											<td className="p-2 border-r">{item.minimum_quantity}</td>
											<td className="p-2 border-r">
												<InputNumber
													min={item.minimum_quantity}
													disabled={isDisabled()}
													value={item.quantity_ordered}
													onChange={(value) =>
														updateMaterialOrderLineItem(
															index,
															'quantity_ordered',
															value
														)
													}
												/>
											</td>
											<td className="p-2 border-r">
												<ShowPrices
													value={item.quantity_ordered * item.unit_price}
												/>
											</td>
											<td className="p-2 border-r">
												<EditableNumberCell
													value={item.length ?? 0}
													onChange={(value) => {

														if(value < (item.min_length ?? 0)){
															notification.warning({
																message: `Length cannot be less than ${item.min_length} length`,
																placement: 'topRight',
																key:'min_length_warning'
															});
															return;
														}
														updateMaterialOrderLineItem(
															index,
															'length',
															value
														)
													}}
													min={item.min_length ?? 0}
													placeholder={`Min ${item.min_length?.toString() }`}
												/>
											</td>

											<td className="p-2 border-r">
												{item.length * item.quantity_ordered}

											</td>


											<td className="p-2 border-r">
												<DatePicker
													disabledDate={(current) =>
														current && current < dayjs().startOf('day')
													}
													disabled={isDisabled()}
													value={
														item.promised_delivery_date
															? dayjs(item.promised_delivery_date)
															: null
													}
													onChange={(date) =>
														updateMaterialOrderLineItem(
															index,
															'promised_delivery_date',
															date
														)
													}
												/>

											</td>

											{(isFirstOccurrence && isDuplicate) ||
											(!isDuplicate &&
												((item.is_merged &&
													Number(item.length) >=
														Number(item.vendor_material_length)) ||
													true)) ? (
												<td
													className="p-2 border-r"
													rowSpan={
														isDuplicate ? duplicateIds.length : undefined
													}
												>
													{item.material_order_requests?.length ? (
														<Checkbox
															checked={
																data
																	? data.preferences[item.vendor_part_number] ==
																	  true
																	: false
															}
															onChange={(e) =>
																handleCheckboxChange(
																	e,
																	item.material_order,
																	item.material_order_requests,
																	item.vendor_part_number
																)
															}
															disabled={
																!item.material_order_requests?.length ||
																isDisabled()
															}
														>
															Round up to full bars
														</Checkbox>
													) : null}
												</td>
											) : null}

											{isDuplicate && isFirstOccurrence ? (
												<td
													className="p-2 border"
													rowSpan={duplicateIds.length}
												>
													{item.material_order_requests &&
														item.material_order_requests.length > 0 && (
															<Button
																type="link"
																onClick={() => {
																	if (item.material_order_requests) {
																		setShowMaterialRequestsRelatedModal(true);
																		setIdsMaterialRequestsRelated(
																			getAllMaterialOrderRequests(
																				item.vendor_part_number
																			)
																		);
																	}
																}}
															>
																View Related Requests
															</Button>
														)}
												</td>
											) : isDuplicate ? null : (
												<td className="p-2 border">
													{item.material_order_requests &&
														item.material_order_requests.length > 0 && (
															<Button
																type="link"
																onClick={() => {
																	if (item.material_order_requests) {
																		setShowMaterialRequestsRelatedModal(true);
																		setIdsMaterialRequestsRelated(
																			getAllMaterialOrderRequests(
																				item.vendor_part_number
																			)
																		);
																	}
																}}
															>
																View Related Requests
															</Button>
														)}
												</td>
											)}

											<td className="p-2 border">
											{!isDraft() ? null : removalWouldEmptyOrder(item) ? (
											<Tooltip title="This is the only item left. Delete the order instead.">
												{/* A disabled button swallows mouse events, so
												    the tooltip needs a wrapper to hang off. */}
												<span>
													<Button
														type="primary"
														danger
														disabled
														icon={<DeleteOutlined />}
													/>
												</span>
											</Tooltip>
											) : (
												<Popconfirm
													title={removalConfirmTitle(item)}
													onConfirm={() => {
														if (item.id) {
															handleDeleteMaterialOrderLineItem(item.id);
														}
													}}
													okText="Yes"
													cancelText="No"
													placement="topRight"
													overlayStyle={{ maxWidth: 340 }}
												>
													<Button
														type="primary"
														danger
														icon={<DeleteOutlined />}
													/>
												</Popconfirm>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
							<tfoot>
								<tr className={'text-right border-b text-xl'}>
									<td colSpan={11} className="p-2 border-r">
										<label className="mr-2">Shipping Cost:</label>
									</td>
									<td colSpan={2} className="p-2 border-r">
										<Form.Item noStyle={true} name="shipping_cost">
											<InputNumber
												min={1}
												disabled={isDisabled()}
												style={{ width: '100%' }}
												onChange={(value) => {
													if (value) {
														refreshTotals(Number(value));
														debouncedUpdateMaterial('shipping_cost', value);
													}
												}}
											/>
										</Form.Item>
									</td>
								</tr>
							</tfoot>
						</table>
					</div>

					<div className="flex flex-row p-4">
						<div className={'w-1/3'} />
						<div className={'w-2/3'}>
							<div className="flex flex-row p-4">
								<div className={'w-2/4'}>
									<Row key={'tax'}>
										<Col xl={12} className="flex flex-col my-4" />
									</Row>
								</div>
								<div className={'w-2/4'}>
									<div className={'flex flex-col w-full'}>
										<Row key={'subtotal'}>
											<Col xl={12} className="flex flex-col my-4" />
											<Col xl={12}>
												<div className={'flex flex-col justify-between '}>
													<div className="flex flex-row justify-between   ">
														<div className="text-sm">Subtotal</div>
														<div className="text-sm">
															<ShowPrices value={Number(totals.subTotal)} />
														</div>
													</div>
												</div>
											</Col>
										</Row>
										<Row key={'subtotal'}>
											<Col xl={12} className="flex flex-col my-4" />
											<Col xl={12}>
												<div className={'flex flex-col justify-between '}>
													<div className="flex flex-row justify-between   ">
														<div className="text-sm">Shipping</div>
														<div className="text-sm">
															<ShowPrices value={Number(totals.shippingCost)} />
														</div>
													</div>
												</div>
											</Col>
										</Row>

										<Row key={'totals'}>
											<Col xl={12} className="flex flex-col my-4" />
											<Col xl={12}>
												<div className={'flex flex-col justify-between '}>
													<div
														className="flex flex-row justify-between   pt-4"
														style={{
															borderTop: 'solid 1px #d9d9d9',
														}}
													>
														<div className="font-bold text-xl">Total</div>
														<div className="font-bold text-xl">
															<ShowPrices value={Number(totals.totalAmount)} />
														</div>
													</div>
												</div>
											</Col>
										</Row>
									</div>
								</div>
							</div>
						</div>
					</div>

					<Divider style={{ margin: 0, padding: '0.5rem' }} />

					<div style={{ display: 'flex', float: 'right' }}>
						{data &&
							(materialOrderStatus === 'In Progress' ||
								materialOrderStatus === 'Submitted') && (
								<div style={{ marginRight: '10px', paddingTop: '15px' }}>
									<Form.Item
										name="create_purchase_order"
										valuePropName="checked"
										style={{ marginTop: '-1rem' }}
										onChange={async (e) => {
											const payload = {
												create_purchase_order: e.target.checked,
											};

											await patchMaterialOrder({
												id: id,
												values: payload,
											});
										}}
									>
										<Checkbox disabled={isDisabled()}>
											<b>Create Purchase Order</b>
										</Checkbox>
									</Form.Item>
								</div>
							)}

						{materialOrderStatus == 'Draft' && (
							<Form.Item>
								<Button
									type="primary"
									loading={deleteMaterialOrderResult?.isLoading}
									danger
									ghost
									onClick={() => {
										showConfirm();
									}}
								>
									Delete
								</Button>
							</Form.Item>
						)}
					</div>

					{data && (
						<Form.Item>
							<Button
								type="default"
								loading={updateMaterialOrderResult?.isLoading}
								icon={<PlusOutlined />}
								onClick={() => setShowVendorMaterialsModal(true)}
								disabled={!(data.vendor && isDraft())}
							>
								Add Vendor Material
							</Button>
							<Tooltip title={addFromMaterialRequestDisabledReason}>
								<span className="ml-4">
									<Button
										type="default"
										onClick={() => setShowAddFromMaterialRequestModal(true)}
										icon={<PlusOutlined />}
										disabled={!!addFromMaterialRequestDisabledReason}
									>
										Add From Material Request
									</Button>
								</span>
							</Tooltip>
						</Form.Item>
					)}

					{!isDisabled() ? (
						<MaterialOrderStatusSteps
							materialOrderLineItems={materialOrderLineItems.length}
							loading={updateMaterialOrderResult.isLoading}
							status={data ? data.status : 'Draft'}
							onFormSubmit={async (status: string) => {
								if (
									status === 'In Progress' &&
									materialOrderLineItems.length === 0
								) {
									if (materialOrderLineItems.length === 0) {
										notification.error({
											duration: 5,
											message: 'Error',
											description:
												'Please add at least one item to the material order',
											placement: 'topRight',
										});
										return;
									}
								}

								if (status === 'Submitted') {
									const errorItems = materialOrderLineItems
										.map((item, index) =>
											item.unit_price == null ||
											item.unit_price === '' ||
											item.promised_delivery_date == null ||
											item.promised_delivery_date === ''
												? { ...item, index: index + 1 }
												: null
										)
										.filter((item) => item !== null);

									if (errorItems.length > 0) {
										notification.error({
											duration: 5,
											message: 'Error',
											description: `Please check the items listed in the table. Unit Price and Promised Delivery Date are required for each item.`,
											placement: 'topRight',
										});
										return;
									}
								}
								setMaterialOrderStatus(status);
								await onFinish(status);
							}}
						/>
					) : null}
				</Card>
			</Form>

			<MaterialOrderRequestsRelated
				visible={showMaterialRequestsRelatedModal}
				idsMaterialRequestsRelated={idsMaterialRequestsRelated}
				onCancel={(value) => setShowMaterialRequestsRelatedModal(value)}
			/>

			<AddFromMaterialRequest
				visible={showAddFromMaterialRequestModal}
				onAddItem={addNewItemFromMaterialRequest}
				material_request_ids={handleGetMaterialRequestsIds()}
				material_vendor_id={data?.vendor ? data?.vendor.id : ''}
				building_id={selectedBuildingId}
				building_name={selectedBuildingName}
				onCancel={(value) => setShowAddFromMaterialRequestModal(value)}
			/>

			<AddFromVendorMaterials
				visible={showVendorMaterialsModal}
				onAddItem={addNewItemFromVendorMaterials}
				vendorPartsNumbers={handleGetVendorMaterialsPartNumbers()}
				material_vendor_id={data?.vendor ? data?.vendor.id : ''}
				onCancel={(value) => setShowVendorMaterialsModal(value)}
			/>
		</>
	);
};

export default AddMaterialOrder;
